<template>
    <main class="grouping-form">
        <header class="grouping-form__header">
            <button type="button" class="icon-button" title="Voltar" @click="back">
                <ArrowLeftIcon />
            </button>
            <div>
                <h1>{{ isEditing ? "Editar agrupamento" : "Novo agrupamento" }}</h1>
                <p>{{ isBackflush ? "Selecione uma OP e as operações para apontar parcialmente pelo ciclo do recurso." : "Configure as ordens, o recurso e a forma de rateio." }}</p>
            </div>
            <span class="draft-pill">{{ isEditing ? "Em edição" : "Rascunho" }}</span>
        </header>

        <div v-if="loading" class="form-state"><LoaderIcon class="spin" /> Carregando</div>

        <div v-else class="grouping-form__layout">
            <div class="grouping-form__main">
                <section class="form-section">
                    <div class="form-section__header">
                        <h2>Dados do agrupamento</h2>
                    </div>

                    <div class="form-grid">
                        <div class="field field--full">
                            <span>Tipo *</span>
                            <div class="segmented" role="group" aria-label="Tipo de agrupamento">
                                <button
                                    type="button"
                                    :class="{ active: form.type === types.NORMAL }"
                                    :disabled="isEditing"
                                    @click="changeType(types.NORMAL)"
                                >Normal</button>
                                <button
                                    type="button"
                                    :class="{ active: form.type === types.NESTING }"
                                    :disabled="isEditing"
                                    @click="changeType(types.NESTING)"
                                >Nesting</button>
                                <button
                                    type="button"
                                    :class="{ active: isBackflush }"
                                    :disabled="isEditing || saving"
                                    @click="changeType(types.BACKFLUSH)"
                                >Backflush</button>
                            </div>
                        </div>

                        <label class="field">
                            <span>Código do agrupamento *</span>
                            <input
                                v-model.trim="form.code"
                                type="text"
                                maxlength="80"
                                :placeholder="isBackflush ? 'Ex: BF-2026-001' : 'Ex: NST-2026-001'"
                                :disabled="isEditing"
                            />
                        </label>

                        <label v-if="form.type === types.NESTING" class="field">
                            <span>Tipo de rateio *</span>
                            <select v-model.number="form.allocationType" @change="resetAllocations">
                                <option :value="allocations.PLANNED_QUANTITY">Quantidade planejada</option>
                                <option :value="allocations.PERCENTAGE">Percentual</option>
                                <option :value="allocations.FIXED_QUANTITY">Quantidade fixa</option>
                            </select>
                        </label>

                        <label class="field" :class="{ 'field--full': form.type !== types.NESTING }">
                            <span>Descrição</span>
                            <input
                                v-model.trim="form.description"
                                type="text"
                                maxlength="500"
                                :placeholder="isBackflush ? 'Ex: Backflush de acabamento' : 'Ex: Nesting de perfis - turno tarde'"
                            />
                        </label>
                    </div>
                </section>

                <section v-if="isBackflush" class="orders-section">
                    <div class="form-section__header orders-section__header">
                        <div>
                            <h2>Ordem de Produção</h2>
                            <p>Selecione uma única OP e marque somente as operações deste backflush.</p>
                        </div>
                        <span v-if="backflushOrder" class="resource-pill">OP {{ backflushOrder.orderNumber }}</span>
                    </div>
                    <div class="form-grid backflush-fields">
                        <label class="field">
                            <span>Pesquisar OP</span>
                            <input v-model.trim="search" type="search" placeholder="Pesquisar por ordem" :disabled="saving" @input="scheduleAvailableLoad" />
                        </label>
                        <label class="field">
                            <span>Ordem de produção *</span>
                            <select v-model.number="backflushOrderId" :disabled="isEditing || saving || backflushLoading" @change="loadBackflushOrder">
                                <option :value="null">Selecione uma OP</option>
                                <option v-for="order in backflushOrders" :key="order.orderId" :value="order.orderId">{{ order.orderNumber }}</option>
                            </select>
                        </label>
                    </div>
                    <div class="orders-section__label">Operações da ordem</div>
                    <div v-if="backflushLoading" class="available-orders__state"><LoaderIcon class="spin" /> Buscando operações</div>
                    <div v-else class="table-scroll">
                        <table class="backflush-table">
                            <thead><tr>
                                <th>Selecionar</th><th>Operação</th><th>Recurso</th>
                                <th class="numeric">Qtd. planejada</th><th>Setup</th><th>Início</th><th>Término</th><th>Situação</th>
                            </tr></thead>
                            <tbody>
                                <tr v-for="operation in backflushOperations" :key="operation.operationId">
                                    <td><input v-model="backflushSelectedIds" type="checkbox" :value="operation.operationId" :aria-label="`Selecionar operação ${operation.number}`" :disabled="saving || !!operation.validationMessage" /></td>
                                    <td><strong>{{ operation.number }} - {{ operation.name }}</strong></td>
                                    <td>{{ operation.resources ? operation.resources.map(resource => resource.resourceName).join(", ") || "-" : operation.resourceName || "-" }}</td>
                                    <td class="numeric">{{ formatNumber(operation.plannedQuantity) }}</td>
                                    <td>{{ formatDate(operation.setupStart) }}</td><td>{{ formatDate(operation.startTime) }}</td><td>{{ formatDate(operation.endTime) }}</td>
                                    <td><span :class="operation.validationMessage ? 'invalid-operation' : 'muted'">{{ operation.validationMessage || (backflushSelectedIds.includes(operation.operationId) ? 'Backflush' : 'Apontamento normal') }}</span></td>
                                </tr>
                                <tr v-if="!backflushOrder"><td colspan="8" class="empty-row">Selecione uma OP para visualizar suas operações.</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="backflush-note">As operações selecionadas ficam ocultas no apontamento normal após salvar. As demais permanecem disponíveis.</p>
                </section>

                <section v-else class="orders-section">
                    <div class="form-section__header orders-section__header">
                        <div>
                            <h2>Ordens de Produção</h2>
                            <p>Uma OP pode participar de mais de um Nesting. Neste agrupamento, todas devem usar o mesmo recurso.</p>
                        </div>
                        <span v-if="resourceName" class="resource-pill">Recurso: {{ resourceName }}</span>
                    </div>

                    <div class="orders-section__label">Ordens do agrupamento</div>
                    <div class="table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>Seq.</th>
                                    <th>Ordem</th>
                                    <th>Operação</th>
                                    <th>Item</th>
                                    <th>Descrição</th>
                                    <th>Outros nestings</th>
                                    <th class="numeric">Qtd. planejada</th>
                                    <th>Rateio</th>
                                    <th class="numeric">Valor rateado</th>
                                    <th aria-label="Ações"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(order, index) in selectedOrders" :key="order.operationId">
                                    <td>{{ index + 1 }}</td>
                                    <td><strong>{{ order.orderNumber }}</strong></td>
                                    <td>{{ order.operationNumber }} - {{ order.operationName }}</td>
                                    <td>{{ order.itemCode }}</td>
                                    <td class="description-cell">{{ order.description }}</td>
                                    <td>
                                        <span v-if="order.otherNestings && order.otherNestings.length" class="nesting-pill">
                                            {{ order.otherNestings.join(", ") }}
                                        </span>
                                        <span v-else class="muted">-</span>
                                    </td>
                                    <td class="numeric">{{ formatNumber(order.plannedQuantity) }}</td>
                                    <td>{{ currentAllocationLabel }}</td>
                                    <td class="numeric">
                                        <input
                                            v-if="requiresAllocationInput"
                                            v-model.number="order.allocationValue"
                                            class="allocation-input"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            aria-label="Valor rateado"
                                        />
                                        <span v-else>{{ formatNumber(order.plannedQuantity) }}</span>
                                    </td>
                                    <td>
                                        <button type="button" class="remove-button" title="Remover ordem" @click="removeOrder(order.operationId)">
                                            <Trash2Icon />
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="selectedOrders.length === 0">
                                    <td colspan="10" class="empty-row">Adicione ao menos uma ordem ao agrupamento.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="orders-section__search">
                        <label class="search-field">
                            <SearchIcon />
                            <input
                                v-model.trim="search"
                                type="search"
                                :placeholder="resourceName ? `Pesquisar ordens de ${resourceName}` : 'Pesquisar por ordem, item, descrição ou recurso'"
                                @input="scheduleAvailableLoad"
                            />
                        </label>
                    </div>

                    <div class="available-orders">
                        <div class="available-orders__title">
                            Ordens disponíveis {{ resourceName ? `- ${resourceName}` : "" }}
                        </div>
                        <div v-if="availableLoading" class="available-orders__state"><LoaderIcon class="spin" /> Buscando ordens</div>
                        <button
                            v-for="order in filteredAvailableOrders"
                            v-else
                            :key="`${order.operationId}-${order.resourceId}`"
                            type="button"
                            class="available-order"
                            @click="addOrder(order)"
                        >
                            <span><strong>OP {{ order.orderNumber }}</strong><small>Operação {{ order.operationNumber }}</small></span>
                            <span class="available-order__description">{{ order.description }}</span>
                            <span v-if="order.otherNestings.length" class="nesting-pill">Também em {{ order.otherNestings.join(", ") }}</span>
                            <span class="available-order__resource">{{ order.resourceName }}</span>
                            <PlusIcon />
                        </button>
                        <div v-if="!availableLoading && filteredAvailableOrders.length === 0" class="available-orders__state">
                            Nenhuma ordem disponível para os filtros informados.
                        </div>
                        <footer v-if="!availableLoading && availableTotalCount > 0" class="available-orders__pagination">
                            <span>{{ availableRangeStart }}-{{ availableRangeEnd }} de {{ availableTotalCount }}</span>
                            <label>
                                Por página
                                <select v-model.number="availablePageSize" @change="changeAvailablePageSize">
                                    <option :value="5">5</option>
                                    <option :value="10">10</option>
                                </select>
                            </label>
                            <div>
                                <button
                                    type="button"
                                    title="Página anterior"
                                    :disabled="availablePage <= 1"
                                    @click="changeAvailablePage(availablePage - 1)"
                                >
                                    <ChevronLeftIcon />
                                </button>
                                <strong>{{ availablePage }} / {{ availableTotalPages }}</strong>
                                <button
                                    type="button"
                                    title="Próxima página"
                                    :disabled="availablePage >= availableTotalPages"
                                    @click="changeAvailablePage(availablePage + 1)"
                                >
                                    <ChevronRightIcon />
                                </button>
                            </div>
                        </footer>
                    </div>
                </section>
            </div>

            <aside class="summary-panel">
                <h2>{{ isBackflush ? "Resumo do agrupamento" : "Resumo do rateio" }}</h2>
                <dl>
                    <div><dt>Quantidade de OPs</dt><dd>{{ isBackflush ? (backflushOrder ? 1 : 0) : selectedOrders.length }}</dd></div>
                    <div v-if="isBackflush"><dt>Operações selecionadas</dt><dd>{{ backflushSelectedIds.length }}</dd></div>
                    <div><dt>Total planejado</dt><dd>{{ formatNumber(totalPlanned) }}</dd></div>
                    <div v-if="!isBackflush"><dt>Total rateado</dt><dd>{{ formatAllocation(totalAllocated) }}</dd></div>
                    <div v-if="!isBackflush"><dt>Diferença</dt><dd :class="{ invalid: difference !== 0 }">{{ formatAllocation(difference) }}</dd></div>
                </dl>
                <hr />
                <dl>
                    <div><dt>Tipo</dt><dd>{{ isBackflush ? "Backflush" : form.type === types.NESTING ? "Nesting" : "Normal" }}</dd></div>
                    <div><dt>Recurso</dt><dd>{{ isBackflush ? "Conforme operação" : resourceName || "-" }}</dd></div>
                    <div><dt>{{ isBackflush ? "Apontamento" : "Rateio" }}</dt><dd>{{ isBackflush ? "Parcial por operação" : currentAllocationLabel }}</dd></div>
                    <div><dt>Status</dt><dd class="summary-panel__status">{{ groupingStatus }}</dd></div>
                </dl>

                <div v-if="error" class="form-error"><AlertCircleIcon /> {{ error }}</div>
                <div v-if="form.type === types.NESTING && difference !== 0" class="form-warning">
                    <AlertCircleIcon /> O rateio deve fechar com diferença zero.
                </div>

                <button type="button" class="save-button" :disabled="!isValid || saving" @click="save">
                    <LoaderIcon v-if="saving" class="spin" />
                    <SaveIcon v-else />
                    {{ isEditing ? "Salvar alterações" : "Salvar agrupamento" }}
                </button>
                <button type="button" class="cancel-button" @click="back">Cancelar</button>
            </aside>
        </div>
    </main>
</template>

<script>
import {
    AlertCircleIcon,
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    LoaderIcon,
    PlusIcon,
    SaveIcon,
    SearchIcon,
    Trash2Icon
} from "vue-feather-icons";
import createOrderGroupingApi, { ALLOCATION_TYPE, ORDER_GROUPING_TYPE } from "./orderGroupingApi";
import createBackflushApi from "./backflushApi";
import { allocationLabel, apiErrorMessage, statusLabel } from "./groupingPresentation";

const allocationFromApi = value =>
    ({
        NONE: ALLOCATION_TYPE.NONE,
        PLANNEDQUANTITY: ALLOCATION_TYPE.PLANNED_QUANTITY,
        PLANNED_QUANTITY: ALLOCATION_TYPE.PLANNED_QUANTITY,
        PERCENTAGE: ALLOCATION_TYPE.PERCENTAGE,
        FIXEDQUANTITY: ALLOCATION_TYPE.FIXED_QUANTITY,
        FIXED_QUANTITY: ALLOCATION_TYPE.FIXED_QUANTITY
    }[value] ?? ALLOCATION_TYPE.PLANNED_QUANTITY);

export default {
    components: {
        AlertCircleIcon,
        ArrowLeftIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        LoaderIcon,
        PlusIcon,
        SaveIcon,
        SearchIcon,
        Trash2Icon
    },
    data() {
        return {
            types: ORDER_GROUPING_TYPE,
            allocations: ALLOCATION_TYPE,
            loading: false,
            availableLoading: false,
            saving: false,
            error: "",
            search: "",
            searchTimer: null,
            availableRequestId: 0,
            availableOrders: [],
            availablePage: 1,
            availablePageSize: 5,
            availableTotalCount: 0,
            availableTotalPages: 1,
            selectedOrders: [],
            backflushOrders: [],
            backflushOrder: null,
            backflushOrderId: null,
            backflushSelectedIds: [],
            backflushLoading: false,
            originalStatus: "WAITING",
            form: {
                code: "",
                type: ORDER_GROUPING_TYPE.NESTING,
                description: "",
                allocationType: ALLOCATION_TYPE.PLANNED_QUANTITY
            }
        };
    },
    computed: {
        isBackflush() {
            return this.form.type === this.types.BACKFLUSH;
        },
        backflushOperations() {
            return this.backflushOrder?.operations || [];
        },
        selectedBackflushOperations() {
            return this.backflushOperations.filter(operation => this.backflushSelectedIds.includes(operation.operationId));
        },
        isEditing() {
            return Boolean(this.$route.params.id);
        },
        siteId() {
            return this.$cookies.get("userInfo")?.currentSite;
        },
        resourceId() {
            return this.selectedOrders[0]?.resourceId || null;
        },
        resourceName() {
            return this.selectedOrders[0]?.resourceName || "";
        },
        filteredAvailableOrders() {
            const selectedIds = new Set(this.selectedOrders.map(order => order.operationId));
            return this.availableOrders.filter(order => !selectedIds.has(order.operationId));
        },
        availableRangeStart() {
            if (this.availableTotalCount === 0) return 0;
            return (this.availablePage - 1) * this.availablePageSize + 1;
        },
        availableRangeEnd() {
            return Math.min(
                this.availableRangeStart + this.filteredAvailableOrders.length - 1,
                this.availableTotalCount
            );
        },
        totalPlanned() {
            if (this.isBackflush) return this.selectedBackflushOperations.reduce((total, operation) => total + Number(operation.plannedQuantity), 0);
            return this.selectedOrders.reduce((total, order) => total + Number(order.plannedQuantity || 0), 0);
        },
        totalAllocated() {
            if (this.form.type === this.types.NORMAL || this.form.allocationType === this.allocations.PLANNED_QUANTITY)
                return this.totalPlanned;

            return this.selectedOrders.reduce((total, order) => total + Number(order.allocationValue || 0), 0);
        },
        difference() {
            if (this.form.type === this.types.NORMAL || this.form.allocationType === this.allocations.PLANNED_QUANTITY)
                return 0;

            const target = this.form.allocationType === this.allocations.PERCENTAGE ? 100 : this.totalPlanned;
            return Number((target - this.totalAllocated).toFixed(4));
        },
        requiresAllocationInput() {
            return this.form.type === this.types.NESTING &&
                this.form.allocationType !== this.allocations.PLANNED_QUANTITY;
        },
        currentAllocationLabel() {
            if (this.form.type === this.types.NORMAL) return "Manual";
            return allocationLabel(
                ({
                    [this.allocations.PLANNED_QUANTITY]: "PLANNEDQUANTITY",
                    [this.allocations.PERCENTAGE]: "PERCENTAGE",
                    [this.allocations.FIXED_QUANTITY]: "FIXEDQUANTITY"
                })[this.form.allocationType]
            );
        },
        groupingStatus() {
            return this.isEditing ? statusLabel(this.originalStatus) : "Rascunho";
        },
        isValid() {
            if (this.isBackflush) return Boolean(this.form.code && this.backflushOrder && !this.backflushLoading &&
                this.selectedBackflushOperations.length > 0 && this.selectedBackflushOperations.every(operation => !operation.validationMessage));
            return Boolean(
                this.form.code &&
                this.selectedOrders.length > 0 &&
                this.difference === 0
            );
        }
    },
    async created() {
        if (this.isEditing) await this.loadGrouping();
        await this.loadAvailableOrders();
    },
    beforeDestroy() {
        window.clearTimeout(this.searchTimer);
    },
    methods: {
        changeType(type) {
            if (this.isEditing) return;
            this.availableRequestId += 1;
            window.clearTimeout(this.searchTimer);
            this.error = "";
            this.search = "";
            this.selectedOrders = [];
            this.backflushOrder = null;
            this.backflushOrderId = null;
            this.backflushSelectedIds = [];
            this.form.type = type;
            this.form.allocationType = type === this.types.NESTING
                ? this.allocations.PLANNED_QUANTITY
                : this.allocations.NONE;
            this.resetAllocations();
            this.loadAvailableOrders();
        },
        resetAllocations() {
            this.selectedOrders.forEach(order => {
                order.allocationValue = this.form.allocationType === this.allocations.PLANNED_QUANTITY
                    ? Number(order.plannedQuantity)
                    : 0;
            });
        },
        scheduleAvailableLoad() {
            window.clearTimeout(this.searchTimer);
            this.availablePage = 1;
            this.searchTimer = window.setTimeout(() => this.loadAvailableOrders(), 300);
        },
        async loadGrouping() {
            this.loading = true;
            this.error = "";

            try {
                const grouping = await createOrderGroupingApi(this.$http2).get(this.$route.params.id);
                if (grouping.status !== "WAITING") {
                    this.$router.replace({
                        name: "operator-order-grouping-detail",
                        params: { prefix: this.$route.params.prefix, id: grouping.id }
                    });
                    return;
                }
                this.form = {
                    code: grouping.code,
                    type: this.types[grouping.type],
                    description: grouping.description,
                    allocationType: allocationFromApi(grouping.allocationType)
                };
                this.originalStatus = grouping.status;
                if (this.isBackflush) {
                    this.backflushOrderId = grouping.orders[0].orderId;
                    this.backflushSelectedIds = grouping.orders.map(order => order.operationId);
                    await this.loadBackflushOrder(true);
                    return;
                }
                this.selectedOrders = grouping.orders.map(order => ({
                    ...order,
                    resourceId: grouping.resourceId,
                    resourceName: grouping.resourceName,
                    otherNestings: [],
                    allocationValue: this.form.allocationType === this.allocations.PERCENTAGE
                        ? Number(order.allocationPercentage || 0)
                        : Number(order.allocatedQuantity ?? order.plannedQuantity)
                }));
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        async loadAvailableOrders() {
            const requestId = ++this.availableRequestId;
            this.availableLoading = true;

            try {
                if (this.isBackflush) {
                    const orders = await createBackflushApi(this.$http2).orders({ siteId: this.siteId, search: this.search || undefined });
                    if (requestId !== this.availableRequestId) return;
                    if (this.backflushOrder && !orders.some(order => order.orderId === this.backflushOrderId)) orders.unshift(this.backflushOrder);
                    this.backflushOrders = orders;
                    return;
                }
                const result = await createOrderGroupingApi(this.$http2).availableOrders({
                    siteId: this.siteId,
                    resourceId: this.resourceId || undefined,
                    search: this.search || undefined,
                    page: this.availablePage,
                    pageSize: this.availablePageSize,
                    excludedOperationIds: this.selectedOrders.map(order => order.operationId).join(",") || undefined
                });

                if (requestId !== this.availableRequestId) return;
                this.availableOrders = result.items || [];
                this.availablePage = result.page || 1;
                this.availablePageSize = result.pageSize || this.availablePageSize;
                this.availableTotalCount = result.totalCount || 0;
                this.availableTotalPages = result.totalPages || 1;
            } catch (error) {
                if (requestId !== this.availableRequestId) return;
                this.error = apiErrorMessage(error);
            } finally {
                if (requestId === this.availableRequestId) this.availableLoading = false;
            }
        },
        async changeAvailablePage(page) {
            const nextPage = Math.min(Math.max(page, 1), this.availableTotalPages);
            if (nextPage === this.availablePage) return;
            this.availablePage = nextPage;
            await this.loadAvailableOrders();
        },
        async changeAvailablePageSize() {
            this.availablePage = 1;
            await this.loadAvailableOrders();
        },
        async addOrder(order) {
            if (this.resourceId && order.resourceId !== this.resourceId) return;

            this.selectedOrders.push({
                ...order,
                allocationValue: this.form.allocationType === this.allocations.PLANNED_QUANTITY
                    ? Number(order.plannedQuantity)
                    : 0
            });
            this.search = "";
            this.availablePage = 1;
            await this.loadAvailableOrders();
        },
        async removeOrder(operationId) {
            this.selectedOrders = this.selectedOrders.filter(order => order.operationId !== operationId);
            this.availablePage = 1;
            await this.loadAvailableOrders();
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString("pt-BR") : "-";
        },
        async loadBackflushOrder(preserveSelection = false) {
            this.error = "";
            this.backflushOrder = null;
            if (preserveSelection !== true) this.backflushSelectedIds = [];
            if (!this.backflushOrderId) return;
            this.backflushLoading = true;
            try {
                this.backflushOrder = await createBackflushApi(this.$http2).get(this.backflushOrderId, this.siteId, this.isEditing ? this.$route.params.id : undefined);
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.backflushLoading = false;
            }
        },
        formatAllocation(value) {
            const formatted = this.formatNumber(value);
            return this.form.type === this.types.NESTING && this.form.allocationType === this.allocations.PERCENTAGE
                ? `${formatted}%`
                : formatted;
        },
        buildPayload() {
            if (this.isBackflush) return {
                code: this.form.code, type: this.types.BACKFLUSH, description: this.form.description,
                siteId: this.siteId, orderId: this.backflushOrderId, allocationType: this.allocations.NONE,
                orders: this.selectedBackflushOperations.map((operation, index) => ({ operationId: operation.operationId, sequence: index + 1 }))
            };
            return {
                code: this.form.code,
                type: this.form.type,
                description: this.form.description,
                allocationType: this.form.type === this.types.NORMAL
                    ? this.allocations.NONE
                    : this.form.allocationType,
                resourceId: this.resourceId,
                siteId: this.siteId,
                orders: this.selectedOrders.map((order, index) => ({
                    operationId: order.operationId,
                    sequence: index + 1,
                    allocatedQuantity: this.form.allocationType === this.allocations.FIXED_QUANTITY
                        ? Number(order.allocationValue || 0)
                        : null,
                    allocationPercentage: this.form.allocationType === this.allocations.PERCENTAGE
                        ? Number(order.allocationValue || 0)
                        : null
                }))
            };
        },
        async save() {
            if (!this.isValid || this.saving) return;

            this.saving = true;
            this.error = "";
            try {
                const api = createOrderGroupingApi(this.$http2);
                const grouping = this.isEditing
                    ? await api.update(this.$route.params.id, this.buildPayload())
                    : await api.create(this.buildPayload());
                this.$router.push({
                    name: "operator-order-grouping-detail",
                    params: { prefix: this.$route.params.prefix, id: grouping.id }
                });
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.saving = false;
            }
        },
        back() {
            const route = this.isEditing ? "operator-order-grouping-detail" : "operator-order-groupings";
            const params = { prefix: this.$route.params.prefix };
            if (this.isEditing) params.id = this.$route.params.id;
            this.$router.push({ name: route, params });
        }
    }
};
</script>

<style lang="scss" scoped>
.grouping-form {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 18px;

        h1 {
            margin: 0;
            font-family: "Raleway";
            font-size: 24px;
            font-weight: 600;
        }

        p {
            margin: 3px 0 0;
            color: #998f8a;
            font-size: 13px;
        }
    }

    &__layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 290px;
        gap: 16px;
        align-items: start;
    }

    &__main {
        min-width: 0;
        display: grid;
        gap: 14px;
    }
}

.icon-button {
    width: 40px;
    height: 40px;
    border: 1px solid #cfc4be;
    border-radius: 5px;
    background: #fff;
    color: #974900;
}

.draft-pill,
.resource-pill,
.nesting-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 12px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 600;
}

.draft-pill {
    margin-left: auto;
    background: #fff5dc;
    color: #835900;
}

.resource-pill {
    background: #fff5dc;
    color: #835900;
}

.nesting-pill {
    background: #f4edff;
    color: #6f42a5;
    white-space: nowrap;
}

.form-section,
.orders-section,
.summary-panel {
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
}

.form-section {
    padding: 18px;
}

.form-section__header {
    h2 {
        margin: 0;
        font-family: "Raleway";
        font-size: 16px;
        font-weight: 600;
    }

    p {
        margin: 4px 0 0;
        color: #998f8a;
        font-size: 12px;
    }
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin-top: 16px;
}

.field {
    min-width: 0;

    &--full {
        grid-column: 1 / -1;
    }

    > span {
        display: block;
        margin-bottom: 6px;
        color: #7e7570;
        font-size: 12px;
    }

    input,
    select {
        width: 100%;
        height: 40px;
        border: 1px solid #cfc4be;
        border-radius: 5px;
        background: #fff;
        padding: 0 10px;
        color: #4c4541;

        &:focus {
            border-color: #974900;
            outline: none;
        }

        &:disabled {
            background: #f3f1f0;
            color: #998f8a;
        }
    }
}

.segmented {
    width: 100%;
    max-width: 360px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    border: 1px solid #cfc4be;
    border-radius: 5px;
    overflow: hidden;

    button {
        height: 40px;
        border: 0;
        background: #fff;
        color: #7e7570;

        + button {
            border-left: 1px solid #cfc4be;
        }

        &.active {
            background: #974900;
            color: #fff;
            font-weight: 600;
        }

        &:disabled:not(.active) {
            background: #f3f1f0;
        }
    }
}

.orders-section {
    overflow: hidden;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 18px;
        border-bottom: 1px solid #ece0db;
    }

    &__label,
    .available-orders__title {
        padding: 8px 14px;
        background: #f7f6f5;
        color: #7e7570;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
    }

    &__search {
        padding: 12px 14px;
        border-top: 1px solid #ece0db;
        border-bottom: 1px solid #ece0db;
    }
}

.table-scroll {
    overflow-x: auto;
}

table {
    width: 100%;
    min-width: 1120px;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 12px;
        border-bottom: 1px solid #f0ecea;
        font-size: 12px;
        text-align: left;
        vertical-align: middle;
    }

    th {
        background: #fbfaf9;
        color: #7e7570;
        font-size: 10px;
        text-transform: uppercase;
    }

    .numeric {
        text-align: right;
    }
}

.description-cell {
    max-width: 230px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.allocation-input {
    width: 82px;
    height: 32px;
    border: 1px solid #cfc4be;
    border-radius: 4px;
    padding: 0 7px;
    text-align: right;
}

.remove-button {
    width: 32px;
    height: 32px;
    border: 0;
    background: transparent;
    color: #b42318;
}

.empty-row,
.available-orders__state {
    padding: 24px !important;
    color: #998f8a;
    text-align: center !important;
}

.search-field {
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #cfc4be;
    border-radius: 5px;
    padding: 0 11px;
    color: #998f8a;

    input {
        width: 100%;
        border: 0;
        outline: 0;
    }
}

.available-order {
    width: 100%;
    min-height: 52px;
    display: grid;
    grid-template-columns: 160px minmax(220px, 1fr) auto 140px 20px;
    align-items: center;
    gap: 12px;
    padding: 8px 14px;
    border: 0;
    border-top: 1px solid #f0ecea;
    background: #fff;
    color: #4c4541;
    text-align: left;

    &:hover {
        background: #fff8f2;
    }

    strong,
    small {
        display: block;
    }

    small {
        color: #998f8a;
    }

    &__description {
        overflow: hidden;
        color: #7e7570;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__resource {
        font-size: 11px;
        font-weight: 600;
        text-align: right;
    }
}

.available-orders__pagination {
    min-height: 54px;
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 16px;
    border-top: 1px solid #ece0db;
    padding: 7px 14px;
    color: #7e7570;
    font-size: 12px;

    label,
    div {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    select {
        height: 34px;
        border: 1px solid #cfc4be;
        border-radius: 4px;
        background: #fff;
        padding: 0 24px 0 8px;
        color: #4c4541;
    }

    button {
        width: 34px;
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #cfc4be;
        border-radius: 4px;
        background: #fff;
        color: #974900;

        svg { width: 17px; height: 17px; }
        &:disabled { opacity: 0.35; }
    }

    strong {
        min-width: 54px;
        color: #4c4541;
        text-align: center;
    }
}

.summary-panel {
    position: sticky;
    top: 12px;
    padding: 18px;

    h2 {
        margin: 0 0 16px;
        font-family: "Raleway";
        font-size: 16px;
        font-weight: 600;
    }

    dl {
        margin: 0;
    }

    dl div {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 11px;
        font-size: 12px;
    }

    dt {
        color: #998f8a;
        font-weight: 400;
    }

    dd {
        margin: 0;
        font-weight: 600;
        text-align: right;

        &.invalid {
            color: #b42318;
        }
    }

    hr {
        margin: 15px 0;
        border: 0;
        border-top: 1px solid #ece0db;
    }

    &__status {
        color: #835900;
    }
}

.form-warning,
.form-error {
    display: flex;
    gap: 7px;
    margin-top: 14px;
    border-radius: 5px;
    padding: 9px;
    font-size: 11px;

    svg {
        flex: 0 0 auto;
    }
}

.form-warning { background: #fff5dc; color: #835900; }
.form-error { background: #fff0ee; color: #b42318; }

.save-button,
.cancel-button {
    width: 100%;
    min-height: 42px;
    border-radius: 5px;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 600;
}

.save-button {
    border: 1px solid #974900;
    background: #974900;
    color: #fff;

    &:disabled {
        opacity: 0.45;
    }
}

.cancel-button {
    border: 1px solid #cfc4be;
    background: #fff;
    color: #7e4510;
}

.form-state {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #998f8a;
}

.muted { color: #b9b1ad; }
.backflush-fields { padding: 0 18px 18px; }
.backflush-table { min-width: 900px; }
.backflush-table input[type="checkbox"] { width: 17px; height: 17px; accent-color: #974900; }
.backflush-note { padding: 14px 18px; margin: 0; color: #998f8a; font-size: 12px; }
.invalid-operation { color: #b42318; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1050px) {
    .grouping-form__layout {
        grid-template-columns: 1fr;
    }

    .summary-panel {
        position: static;
    }
}

@media (max-width: 700px) {
    .grouping-form__header {
        align-items: flex-start;

        h1 {
            font-size: 20px;
        }
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .field--full {
        grid-column: auto;
    }

    .orders-section__header {
        align-items: flex-start;
        flex-direction: column;
    }

    .available-order {
        grid-template-columns: minmax(0, 1fr) auto;

        &__description,
        &__resource,
        .nesting-pill {
            grid-column: 1;
        }

        > svg {
            grid-column: 2;
            grid-row: 1 / span 4;
        }
    }

    .available-orders__pagination {
        grid-template-columns: 1fr auto;

        > span { grid-column: 1 / -1; }
        > div { justify-self: end; }
    }
}
</style>
